import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '..'
import { SearchReviewType } from '../../constants/constants'
import { Language } from '../../types/generalTypes'
import { SourceType } from './dataSourceReducer'
import { User } from './userReducer'

export interface Document {
  id: string
  oid: string | null
  urn: string
  authors: string[]
  dataSource?: string
  csv: string
  documentLink: string
  language: Language | null
  publicationDate: string | null
  publicationName: string | null
  title: string
  summary?: string
  tags?: string
  summaryStatus?: string
  description?: string
  literatureReview: string
  searchEvent: string
  tagDetails?: TagDetails
}

export interface TagDetails {
  [key: string]: {
    count: number
    score: number
  }
}

export interface SearchParameter {
  dataType: string[]
  date: string
  language: string
  page: number
  pageSize: number
  searchMethod: string
  searchQuery: string
  source: number
}

export interface SearchSourceConfig {
  authorsKey: string
  languageKey?: string
  databaseName: string
  authorNameKey: string
  documentIdKey?: string
  documentLinkBase: string
  documentTitleKey: string
  publicationDateKey: string
  publicationNameKey: string
}

export interface SearchSource {
  id: string
  clusterId: string | null
  externalId: string | null
  oid: string
  sourceType: SourceType
  title: string
  description: string | null
  visibility: string
  organizationName: string
  contactName: string | null
  contactEmail: string | null
  config: SearchSourceConfig
  defaultSource: boolean
  participants: User[]
  createdBy: string
  lastModifiedBy: string
  createdDate: string
  lastModifiedDate: string
  typeName: string
  typeCode: string
  isInternal: boolean
}

export interface SearchQuery {
  id: string
  searchActionBy: User
  searchDate: string
  searchDescription: string
  searchEvent: string
  searchParameters: SearchParameter
  searchResults: Document[]
  searchSource: SearchSource
  searchText: string
  uiFlags: number
  literatureReview: string
}

export interface SearchReview {
  id: string
  oid: string | null
  createdBy?: User
  createdDate?: string
  description?: string | null
  includedSearchResults?: string[]
  lastModifiedBy?: string
  lastModifiedDate?: string
  searchQueries?: SearchQuery[]
  title?: string
  uiFlags?: number
  reviewType: SearchReviewType
}

export interface SearchReviewListResponse {
  data: SearchReview[]
  totalResults: number
  page: number
  pageSize: number
  totalPages: number
  uiFlags: number
}

export interface SearchReviewState {
  searchReviewList?: SearchReview[]
  searchReviewListByIds?: SearchReview[]
  selected?: SearchReview
  selectedSearchQuery?: SearchQuery
  selectedSearchResultItem?: Document
  detailsCsv?: string
  listCsv?: string
  searchQueryCsv?: string
  searchQueryLoading?: boolean
  loading?: boolean
  searchReviewListTotalResults?: number
  uiFlags?: number
}

const initialState: SearchReviewState = {
  searchReviewList: undefined,
  searchReviewListByIds: undefined,
  selected: undefined,
  selectedSearchQuery: undefined,
  selectedSearchResultItem: undefined,
  detailsCsv: undefined,
  listCsv: undefined,
  searchQueryCsv: undefined,
  searchQueryLoading: false,
  loading: false,
  searchReviewListTotalResults: 0,
  uiFlags: 0,
}

const searchReviewSlice = createSlice({
  name: 'searchReviews',
  initialState,
  reducers: {
    setSearchReviewList: (state, action: PayloadAction<SearchReview[]>) => {
      state.searchReviewList = action.payload
    },
    setSearchReviewListByIds: (
      state,
      action: PayloadAction<SearchReview[]>
    ) => {
      state.searchReviewListByIds = action.payload
    },
    setSelectedSearchReview: (state, action: PayloadAction<SearchReview>) => {
      state.selected = action.payload
    },
    setSelectedSearchQuery: (state, action: PayloadAction<SearchQuery>) => {
      state.selectedSearchQuery = action.payload
    },
    setSelectedSearchResultItem: (state, action: PayloadAction<Document>) => {
      state.selectedSearchResultItem = action.payload
    },
    setSearchQueryLoading: (state, action: PayloadAction<boolean>) => {
      state.searchQueryLoading = action.payload
    },
    setSearchReviewLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setListCsv: (state, action: PayloadAction<string>) => {
      state.listCsv = action.payload
    },
    setSearchQueryCsv: (state, action: PayloadAction<string>) => {
      state.searchQueryCsv = action.payload
    },
    setDetailsCsv: (state, action: PayloadAction<string>) => {
      state.detailsCsv = action.payload
    },
    setSearchReviewListUiFlags: (state, action: PayloadAction<number>) => {
      state.uiFlags = action.payload
    },
    setSearchReviewListTotalResults: (state, action: PayloadAction<number>) => {
      state.searchReviewListTotalResults = action.payload
    },
    clearSearchReviewState: () => initialState,
  },
})

export const {
  setSearchReviewList,
  setSelectedSearchReview,
  setSelectedSearchQuery,
  setSelectedSearchResultItem,
  setSearchQueryLoading,
  setSearchReviewLoading,
  clearSearchReviewState,
  setListCsv,
  setSearchQueryCsv,
  setDetailsCsv,
  setSearchReviewListTotalResults,
  setSearchReviewListByIds,
  setSearchReviewListUiFlags,
} = searchReviewSlice.actions

export const getSearchReview = (state: RootState) => state.searchReview

const searchReviewReducer = searchReviewSlice.reducer
export default searchReviewReducer
